import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import styles from "./SerchingPartner.module.css";
import UserProfilePopup from "./UserProfilePopup";

import {
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

/* =========================================================
   API CONFIG
========================================================= */

const PARTNER_API_URL =
  "https://coinyoco.world/brobizee-api/get_partnerdata.php";

const FILL_SENDER_API_URL =
  "https://coinyoco.world/brobizee-api/fill_sender_details.php";

const GET_SENDER_REQUEST_URL =
  "https://coinyoco.world/brobizee-api/get_sender_request.php";

/* ⭐ Save sender chatbox message */
const SAVE_SENDER_CHATBOX_URL =
  "https://coinyoco.world/brobizee-api/update_sender_chatbox.php";

/* =========================================================
   MAX SEARCH DISTANCE
========================================================= */

const MAX_PARTNER_DISTANCE_KM = 20;

/* =========================================================
   DISTANCE CALCULATOR
========================================================= */

const calculateDistanceKm = (
  latitude1,
  longitude1,
  latitude2,
  longitude2
) => {
  const earthRadiusKm = 6371;

  const lat1 = Number(latitude1);
  const lon1 = Number(longitude1);
  const lat2 = Number(latitude2);
  const lon2 = Number(longitude2);

  if (
    !Number.isFinite(lat1) ||
    !Number.isFinite(lon1) ||
    !Number.isFinite(lat2) ||
    !Number.isFinite(lon2)
  ) {
    return null;
  }

  const latitudeDifference =
    ((lat2 - lat1) * Math.PI) / 180;

  const longitudeDifference =
    ((lon2 - lon1) * Math.PI) / 180;

  const a =
    Math.sin(latitudeDifference / 2) *
      Math.sin(latitudeDifference / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(longitudeDifference / 2) *
      Math.sin(longitudeDifference / 2);

  const c =
    2 *
    Math.atan2(
      Math.sqrt(a),
      Math.sqrt(1 - a)
    );

  return earthRadiusKm * c;
};

/* =========================================================
   NORMALIZE TEXT
========================================================= */

const normalizeText = (value) => {
  return String(value || "")
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ");
};

/* =========================================================
   SERVICE WORDS
========================================================= */

const getServiceWords = (value) => {
  const normalized = normalizeText(value);

  if (!normalized) return [];

  return normalized
    .split(" ")
    .map((word) => word.trim())
    .filter(
      (word) =>
        word.length >= 3 &&
        ![
          "and",
          "the",
          "for",
          "with",
          "service",
          "services",
          "work",
          "works",
          "professional",
          "professionals",
        ].includes(word)
    );
};

/* =========================================================
   SERVICE ALIAS MAP
========================================================= */

const SERVICE_ALIASES = {
  carpenter: ["carpenter", "carpentry", "wood", "furniture"],
  carpentry: ["carpenter", "carpentry", "wood", "furniture"],

  electrician: ["electrician", "electrical", "electric", "wiring"],
  electrical: ["electrician", "electrical", "electric", "wiring"],

  plumber: ["plumber", "plumbing", "pipe", "pipes", "water"],
  plumbing: ["plumber", "plumbing", "pipe", "pipes", "water"],

  mechanic: ["mechanic", "mechanical", "automobile", "auto", "vehicle"],
  mechanical: ["mechanic", "mechanical", "automobile", "auto", "vehicle"],

  maid: ["maid", "cleaning", "cleaner", "housekeeping", "household"],
  cleaning: ["cleaning", "cleaner", "maid", "housekeeping"],

  painter: ["painter", "painting", "paint"],
  painting: ["painter", "painting", "paint"],

  driver: ["driver", "driving", "transport", "transportation"],
  driving: ["driver", "driving", "transport", "transportation"],

  cook: ["cook", "cooking", "chef", "food"],
  cooking: ["cook", "cooking", "chef", "food"],

  gardener: ["gardener", "gardening", "garden", "landscaping"],
  gardening: ["gardener", "gardening", "garden", "landscaping"],
};

/* =========================================================
   SHORT SERVICE NAME
========================================================= */

const getShortServiceName = (value) => {
  const text = String(value || "").trim();

  if (!text) return "Service";
  if (text.length <= 22) return text;

  const words = text.split(/\s+/);
  let result = "";

  for (const word of words) {
    const next =
      result.length > 0 ? `${result} ${word}` : word;
    if (next.length > 21) break;
    result = next;
  }

  if (!result) result = text.substring(0, 21);

  return `${result}…`;
};

/* =========================================================
   PARSE PARTNER LOCATION
========================================================= */

const parsePartnerCoordinates = (partner) => {
  if (!partner || typeof partner !== "object") return null;

  const directLatitude =
    partner.latitude ??
    partner.lat ??
    partner.Latitude ??
    partner.Lat;

  const directLongitude =
    partner.longitude ??
    partner.lng ??
    partner.lon ??
    partner.Longitude ??
    partner.Lng ??
    partner.Lon;

  if (
    directLatitude !== undefined &&
    directLatitude !== null &&
    directLongitude !== undefined &&
    directLongitude !== null
  ) {
    const latitude = Number(directLatitude);
    const longitude = Number(directLongitude);

    if (
      Number.isFinite(latitude) &&
      Number.isFinite(longitude) &&
      latitude >= -90 &&
      latitude <= 90 &&
      longitude >= -180 &&
      longitude <= 180
    ) {
      return { latitude, longitude };
    }
  }

  const possibleLocationValues = [
    partner.latitude_longitude,
    partner.latitudeLongitude,
    partner.lat_long,
    partner.lat_lng,
    partner.coordinates,
    partner.location,
    partner.current_location,
    partner.currentLocation,
    partner.gps_location,
    partner.gpsLocation,
  ];

  for (const value of possibleLocationValues) {
    if (!value) continue;

    if (typeof value === "object" && !Array.isArray(value)) {
      const latitude = Number(
        value.latitude ??
          value.lat ??
          value.Latitude ??
          value.Lat
      );
      const longitude = Number(
        value.longitude ??
          value.lng ??
          value.lon ??
          value.Longitude ??
          value.Lng ??
          value.Lon
      );

      if (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
      ) {
        return { latitude, longitude };
      }
    }

    const text = String(value).trim();
    const match = text.match(
      /(-?\d+(?:\.\d+)?)\s*,\s*(-?\d+(?:\.\d+)?)/
    );

    if (match) {
      const latitude = Number(match[1]);
      const longitude = Number(match[2]);

      if (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= -90 &&
        latitude <= 90 &&
        longitude >= -180 &&
        longitude <= 180
      ) {
        return { latitude, longitude };
      }
    }
  }

  return null;
};

/* =========================================================
   PARTNER GETTERS
========================================================= */

const getPartnerName = (partner) =>
  partner?.full_name ||
  partner?.name ||
  partner?.partner_name ||
  partner?.fullName ||
  "BroBizee Partner";

const getPartnerWork = (partner, fallbackService) =>
  partner?.work ||
  partner?.secondary_work ||
  partner?.recently_work ||
  partner?.service ||
  partner?.service_name ||
  partner?.category ||
  partner?.category_name ||
  fallbackService ||
  "Professional Service";

const getPartnerExperience = (partner) =>
  partner?.experiences ||
  partner?.experience ||
  partner?.experience_years ||
  partner?.exp ||
  "";

const getPartnerStatus = (partner) =>
  partner?.online_stutes ||
  partner?.online_status ||
  partner?.onlineStatus ||
  partner?.status ||
  partner?.stutes ||
  "";

const getPartnerAge = (partner) =>
  partner?.age || partner?.Age || "";

const getPartnerGender = (partner) =>
  partner?.gender || partner?.Gender || "";

const getPartnerRating = (partner) =>
  partner?.rating ??
  partner?.ratings ??
  partner?.review_rating ??
  partner?.average_rating ??
  partner?.avg_rating ??
  partner?.rate ??
  "";

const getPartnerViews = (partner) =>
  partner?.views ??
  partner?.view_count ??
  partner?.profile_views ??
  partner?.total_views ??
  "";

const getPartnerCharges = (partner) =>
  partner?.charges ??
  partner?.charge ??
  partner?.price ??
  partner?.rate_per_hour ??
  partner?.hourly_rate ??
  partner?.money ??
  partner?.profit ??
  "";

const getPartnerTeam = (partner) =>
  partner?.team ??
  partner?.team_size ??
  partner?.helper_list ??
  partner?.helpers ??
  "";

const getPartnerMode = (partner) =>
  partner?.mode ??
  partner?.work_mode ??
  partner?.vehicle_mode ??
  partner?.transport_mode ??
  "";

const getPartnerProfile = (partner) =>
  partner?.profile ||
  partner?.profile_image ||
  partner?.profileImage ||
  partner?.avatar ||
  partner?.image ||
  "";

/* =========================================================
   ONLINE STATUS
========================================================= */

const isPartnerOnline = (status) => {
  const normalized = normalizeText(status);
  if (!normalized) return null;

  return [
    "online",
    "on",
    "on duty",
    "available",
    "active",
    "working",
    "1",
    "true",
  ].includes(normalized);
};

/* =========================================================
   SERVICE MATCH
========================================================= */

const doesPartnerMatchService = (
  partner,
  selectedService
) => {
  const selected = normalizeText(selectedService);
  if (!selected) return false;

  const partnerValues = [
    partner?.work,
    partner?.secondary_work,
    partner?.recently_work,
    partner?.service,
    partner?.service_name,
    partner?.category,
    partner?.category_name,
    partner?.position,
    partner?.job_title,
    partner?.profession,
  ]
    .filter(Boolean)
    .map(normalizeText)
    .filter(Boolean);

  if (partnerValues.length === 0) return false;

  const directMatch = partnerValues.some(
    (partnerService) =>
      partnerService === selected ||
      partnerService.includes(selected) ||
      selected.includes(partnerService)
  );

  if (directMatch) return true;

  const selectedWords = getServiceWords(selected);
  const partnerWords = partnerValues.flatMap((v) =>
    getServiceWords(v)
  );

  const commonWord = selectedWords.some((w) =>
    partnerWords.includes(w)
  );

  if (commonWord) return true;

  const aliases = new Set();

  Object.keys(SERVICE_ALIASES).forEach((key) => {
    if (selected.includes(key) || key.includes(selected)) {
      SERVICE_ALIASES[key].forEach((alias) =>
        aliases.add(alias)
      );
    }
  });

  if (aliases.size > 0) {
    return partnerValues.some((partnerService) =>
      Array.from(aliases).some((alias) =>
        partnerService.includes(normalizeText(alias))
      )
    );
  }

  return false;
};

/* =========================================================
   DISTANCE FORMAT
========================================================= */

const formatDistance = (distance) => {
  if (
    distance === null ||
    distance === undefined ||
    !Number.isFinite(distance)
  ) {
    return "Distance unavailable";
  }

  if (distance < 1) return `${Math.round(distance * 1000)} m`;

  return `${distance.toFixed(1)} km`;
};

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function SerchingPartner({
  onBack,
  onPass,
  onConfirm,
}) {
  const navigate = useNavigate();

  /* URL Params */
  const params = useParams();

  const routeUserId =
    params?.userId || params?.userIdA || "";

  const routeUuid =
    params?.uuid || params?.deviceUuid || "";

  /* TIMER */
  const [timer, setTimer] = useState(60);
  const [showBusyPopup, setShowBusyPopup] = useState(false);

  /* PARTNER STATES */
  const [partners, setPartners] = useState([]);
  const [nearbyPartners, setNearbyPartners] = useState([]);
  const [partnerLoading, setPartnerLoading] = useState(false);
  const [partnerError, setPartnerError] = useState("");
  const [showPartnerPopup, setShowPartnerPopup] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);

  /* PARTNER BUSY POPUP STATE */
  const [showPartnerBusyPopup, setShowPartnerBusyPopup] =
    useState(false);

  /* WAITING FOR PARTNER POPUP STATE */
  const [showWaitingPopup, setShowWaitingPopup] =
    useState(false);

  /* USER PROFILE POPUP STATE */
  const [showUserProfile, setShowUserProfile] = useState(false);
  const [profileUserId, setProfileUserId] = useState("");
  const [profileDeviceUuid, setProfileDeviceUuid] = useState("");

  /* SENDER REQUEST VALUE */
  const [senderRequestValue, setSenderRequestValue] =
    useState(null);

  /* ⭐ SENDER CHATBOX MESSAGE (unchanged backend link) */
  const [senderChatboxMessage, setSenderChatboxMessage] =
    useState("");
  const [senderChatboxSaving, setSenderChatboxSaving] =
    useState(false);
  const [senderChatboxSaved, setSenderChatboxSaved] =
    useState(false);

  /* ⭐ Floating chat panel state */
  const [chatPanelOpen, setChatPanelOpen] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatMessages, setChatMessages] = useState([
    {
      id: 1,
      from: "bot",
      text:
        "Hi 👋 I'm BroBizee Assistant. Tell me what you need — e.g. 'Please bring your own tools' — and I'll attach it to your partner request.",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    },
  ]);
  const chatEndRef = useRef(null);

  /* CURRENT LOCATION */
  const [location, setLocation] = useState({
    latitude: null,
    longitude: null,
  });

  const [locationAddress, setLocationAddress] = useState(
    "Getting your current location..."
  );

  const [locationError, setLocationError] = useState("");
  const [permissionState, setPermissionState] = useState("checking");
  const [locationLoading, setLocationLoading] = useState(true);

  /* ROUTER LOCATION */
  const routerLocation = useLocation();

  const serviceName =
    routerLocation.state?.serviceName ||
    localStorage.getItem("selected_service_name") ||
    "Carpenter";

  const serviceIcon =
    routerLocation.state?.serviceIcon ||
    localStorage.getItem("selected_service_icon") ||
    "";

  const shortServiceName = getShortServiceName(serviceName);

  /* SEARCH MESSAGES */
  const searchMessages = [
    "Thank you for Waiting ..",
    "Searching Nearby Partners ..",
    "Finding the Best Partner for You ..",
    "Checking Partner Availability ..",
    "Connecting With a Suitable Partner ..",
    "Partner is on the Way ..",
    "Looking for the Right Partner for You ..",
    "Almost There ..",
  ];

  const [searchMessageIndex, setSearchMessageIndex] = useState(0);

  /* LATEST PARTNER REF */
  const nearbyPartnersRef = useRef([]);

  useEffect(() => {
    nearbyPartnersRef.current = nearbyPartners;
  }, [nearbyPartners]);

  /* ⭐ Keep backend var in sync with chat input */
  useEffect(() => {
    setSenderChatboxMessage(chatInput.trim());
    setSenderChatboxSaved(false);
  }, [chatInput]);

  /* ⭐ Auto-scroll chat to bottom */
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({
        behavior: "smooth",
        block: "end",
      });
    }
  }, [chatMessages, chatPanelOpen]);

  /* BACK → /userprofile */
  const handleBack = () => {
    if (typeof onBack === "function") {
      onBack();
      return;
    }

    if (routeUserId && routeUuid) {
      navigate(`/${routeUserId}/${routeUuid}/userprofile`);
    } else {
      navigate("/userprofile");
    }
  };

  /* TIMER */
  useEffect(() => {
    const interval = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(interval);

          const available = nearbyPartnersRef.current;

          if (available && available.length > 0) {
            setShowBusyPopup(false);
            setShowPartnerPopup(true);
          } else {
            setShowPartnerPopup(false);
            setShowBusyPopup(true);
          }

          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  /* MESSAGE ROTATION */
  useEffect(() => {
    const interval = setInterval(() => {
      setSearchMessageIndex(
        (prev) => (prev + 1) % searchMessages.length
      );
    }, 2500);

    return () => clearInterval(interval);
  }, [searchMessages.length]);

  /* ⭐ Chat send handler */
  const handleSendChatMessage = () => {
    const text = chatInput.trim();
    if (!text) return;

    const now = new Date().toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });

    const userMsg = {
      id: Date.now(),
      from: "user",
      text,
      time: now,
    };

    setChatMessages((prev) => [...prev, userMsg]);

    setTimeout(() => {
      const botMsg = {
        id: Date.now() + 1,
        from: "bot",
        text:
          "Got it ✅ I'll attach this message to your partner request so the partner knows exactly what you need.",
        time: new Date().toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
      };

      setChatMessages((prev) => [...prev, botMsg]);
    }, 450);

    setChatInput("");
  };

  /* REVERSE GEOCODING */
  const getAddressFromCoordinates = useCallback(
    async (latitude, longitude) => {
      try {
        const response = await fetch(
          `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&addressdetails=1`,
          { headers: { Accept: "application/json" } }
        );

        if (!response.ok) throw new Error("Address lookup failed");

        const data = await response.json();
        const address = data.address || {};

        const area =
          address.suburb ||
          address.neighbourhood ||
          address.village ||
          address.town ||
          address.city_district ||
          "";

        const road =
          address.road ||
          address.residential ||
          address.hamlet ||
          "";

        const city =
          address.city ||
          address.town ||
          address.municipality ||
          address.village ||
          "";

        const state = address.state || "";
        const postcode = address.postcode || "";

        const parts = [area, road, city, state, postcode].filter(Boolean);

        if (parts.length > 0) {
          setLocationAddress(parts.join(", "));
        } else if (data.display_name) {
          setLocationAddress(data.display_name);
        } else {
          setLocationAddress("Current location detected");
        }
      } catch (error) {
        console.error("Reverse geocoding error:", error);
        setLocationAddress("Current location detected");
      }
    },
    []
  );

  /* FETCH + FILTER PARTNERS */
  const fetchNearbyPartners = useCallback(
    async (
      latitudeOverride = null,
      longitudeOverride = null,
      shouldOpenPopup = true
    ) => {
      const latitude = latitudeOverride ?? location.latitude;
      const longitude = longitudeOverride ?? location.longitude;

      if (
        latitude === null ||
        longitude === null ||
        latitude === undefined ||
        longitude === undefined
      ) {
        return;
      }

      setPartnerLoading(true);
      setPartnerError("");

      try {
        const response = await fetch(PARTNER_API_URL, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

        if (!response.ok) {
          throw new Error(
            `Partner API failed with status ${response.status}`
          );
        }

        const result = await response.json();

        let rawPartners = [];

        if (Array.isArray(result)) rawPartners = result;
        else if (Array.isArray(result?.data)) rawPartners = result.data;
        else if (Array.isArray(result?.partners))
          rawPartners = result.partners;
        else if (Array.isArray(result?.results))
          rawPartners = result.results;
        else if (Array.isArray(result?.partnerdata))
          rawPartners = result.partnerdata;

        setPartners(rawPartners);

        const processedPartners = rawPartners
          .map((partner, index) => {
            const coordinates = parsePartnerCoordinates(partner);
            if (!coordinates) return null;

            const distance = calculateDistanceKm(
              latitude,
              longitude,
              coordinates.latitude,
              coordinates.longitude
            );

            if (distance === null) return null;

            const serviceMatch = doesPartnerMatchService(
              partner,
              serviceName
            );

            return {
              ...partner,
              _originalIndex: index,
              _latitude: coordinates.latitude,
              _longitude: coordinates.longitude,
              _distanceKm: distance,
              _serviceMatch: serviceMatch,
              _partnerName: getPartnerName(partner),
              _partnerWork: getPartnerWork(partner, serviceName),
              _partnerExperience: getPartnerExperience(partner),
              _partnerStatus: getPartnerStatus(partner),
              _partnerAge: getPartnerAge(partner),
              _partnerGender: getPartnerGender(partner),
              _partnerRating: getPartnerRating(partner),
              _partnerViews: getPartnerViews(partner),
              _partnerCharges: getPartnerCharges(partner),
              _partnerTeam: getPartnerTeam(partner),
              _partnerMode: getPartnerMode(partner),
              _partnerProfile: getPartnerProfile(partner),
            };
          })
          .filter(Boolean);

        const matchingNearbyPartners = processedPartners
          .filter(
            (partner) =>
              partner._distanceKm <= MAX_PARTNER_DISTANCE_KM &&
              partner._serviceMatch === true
          )
          .sort((a, b) => {
            const onlineA = isPartnerOnline(a._partnerStatus);
            const onlineB = isPartnerOnline(b._partnerStatus);

            if (onlineA !== onlineB) {
              if (onlineA === true) return -1;
              if (onlineB === true) return 1;
            }

            return a._distanceKm - b._distanceKm;
          });

        setNearbyPartners(matchingNearbyPartners);

        if (matchingNearbyPartners.length === 0) {
          setSelectedPartner(null);
          setShowPartnerPopup(false);
          if (shouldOpenPopup) setShowBusyPopup(true);
          return;
        }

        setShowBusyPopup(false);
        if (shouldOpenPopup) setShowPartnerPopup(true);
      } catch (error) {
        console.error("Partner fetch error:", error);

        setPartnerError(
          "Sorry, partner information could not be loaded."
        );

        setPartners([]);
        setNearbyPartners([]);
        setSelectedPartner(null);

        if (shouldOpenPopup) {
          setShowPartnerPopup(false);
          setShowBusyPopup(true);
        }
      } finally {
        setPartnerLoading(false);
      }
    },
    [location.latitude, location.longitude, serviceName]
  );

  /* CURRENT LOCATION */
  const getCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setPermissionState("unsupported");
      setLocationError(
        "Your device/browser does not support location."
      );
      setLocationAddress("Location not available");
      setLocationLoading(false);
      return;
    }

    setLocationLoading(true);
    setLocationError("");
    setLocationAddress("Getting your current location...");

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const latitude = position.coords.latitude;
        const longitude = position.coords.longitude;

        setLocation({ latitude, longitude });
        setPermissionState("granted");

        localStorage.setItem("current_latitude", latitude.toString());
        localStorage.setItem("current_longitude", longitude.toString());
        localStorage.setItem(
          "current_latitude_longitude",
          `${latitude},${longitude}`
        );

        await getAddressFromCoordinates(latitude, longitude);
        setLocationLoading(false);

        await fetchNearbyPartners(latitude, longitude, true);
      },
      (error) => {
        console.error("Geolocation error:", error);
        setLocationLoading(false);

        switch (error.code) {
          case error.PERMISSION_DENIED:
            setPermissionState("denied");
            setLocationAddress("Location permission is required");
            setLocationError(
              "Please allow Location Permission from your browser/device settings."
            );
            break;
          case error.POSITION_UNAVAILABLE:
            setPermissionState("unavailable");
            setLocationAddress("Current location unavailable");
            setLocationError(
              "Please turn ON your device Location/GPS and try again."
            );
            break;
          case error.TIMEOUT:
            setPermissionState("timeout");
            setLocationAddress("Location request timed out");
            setLocationError(
              "Please make sure Location/GPS is ON and try again."
            );
            break;
          default:
            setPermissionState("error");
            setLocationAddress("Location not available");
            setLocationError("Unable to get your current location.");
        }
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  }, [getAddressFromCoordinates, fetchNearbyPartners]);

  /* CHECK LOCATION PERMISSION */
  useEffect(() => {
    let permissionStatus = null;
    let mounted = true;

    const checkLocationPermission = async () => {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          permissionStatus = await navigator.permissions.query({
            name: "geolocation",
          });

          if (!mounted) return;

          setPermissionState(permissionStatus.state);

          if (permissionStatus.state === "granted") {
            getCurrentLocation();
          } else if (permissionStatus.state === "prompt") {
            getCurrentLocation();
          } else if (permissionStatus.state === "denied") {
            setLocationLoading(false);
            setLocationAddress("Location Permission Denied");
            setLocationError(
              "Please enable Location Permission in your browser/device settings."
            );
          }

          permissionStatus.onchange = () => {
            if (!mounted) return;
            setPermissionState(permissionStatus.state);
            if (permissionStatus.state === "granted") {
              getCurrentLocation();
            }
          };

          return;
        } catch (error) {
          console.warn("Permissions API unavailable:", error);
        }
      }

      if (mounted) getCurrentLocation();
    };

    checkLocationPermission();

    return () => {
      mounted = false;
      if (permissionStatus) permissionStatus.onchange = null;
    };
  }, [getCurrentLocation]);

  /* LOCATION RETRY */
  const handleEnableLocation = () => {
    setLocationError("");
    setLocationAddress("Requesting Location Permission...");
    setLocationLoading(true);
    getCurrentLocation();
  };

  /* LOCATION AVAILABLE */
  const hasLocation =
    location.latitude !== null && location.longitude !== null;

  /* REFRESH PARTNERS */
  const handleRefreshPartners = async () => {
    if (!hasLocation) {
      handleEnableLocation();
      return;
    }

    await fetchNearbyPartners(
      location.latitude,
      location.longitude,
      true
    );
  };

  /* FIND AGAIN */
  const handleFindPartnerAgain = async () => {
    setShowBusyPopup(false);
    setShowPartnerPopup(false);
    setSelectedPartner(null);
    setShowPartnerBusyPopup(false);
    setShowWaitingPopup(false);

    setTimer(60);

    if (hasLocation) {
      await fetchNearbyPartners(
        location.latitude,
        location.longitude,
        true
      );
    } else {
      getCurrentLocation();
    }
  };

  /* Save sender chatbox message separately */
  const saveSenderChatbox = useCallback(
    async (partner, message) => {
      if (!partner || !message?.trim()) return false;

      const partnerSecondaryId =
        partner?.secondary_id || partner?.id || "";

      const partnerSecondaryUuid =
        partner?.secondary_uuid || "";

      if (!partnerSecondaryId || !partnerSecondaryUuid) {
        return false;
      }

      setSenderChatboxSaving(true);

      try {
        const formData = new FormData();

        formData.append(
          "partner_secondary_id",
          String(partnerSecondaryId)
        );

        formData.append(
          "partner_secondary_uuid",
          String(partnerSecondaryUuid)
        );

        formData.append(
          "sender_chatbox",
          String(message).trim()
        );

        const response = await fetch(
          SAVE_SENDER_CHATBOX_URL,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();

        if (!result?.success) {
          console.warn(
            "sender_chatbox save failed:",
            result?.message
          );
          return false;
        }

        setSenderChatboxSaved(true);
        return true;
      } catch (error) {
        console.warn(
          "saveSenderChatbox error:",
          error
        );
        return false;
      } finally {
        setSenderChatboxSaving(false);
      }
    },
    []
  );

  /* =========================================================
     ⭐ SELECT PARTNER + FILL SENDER DETAILS
     Saves the partner's charges into `sender_money` column
  ========================================================= */

  const handleSelectPartner = async (partner) => {
    setSelectedPartner(partner);

    localStorage.setItem(
      "selected_partner",
      JSON.stringify(partner)
    );

    localStorage.setItem(
      "selected_partner_id",
      String(partner.id || partner.secondary_id || "")
    );

    localStorage.setItem(
      "selected_partner_uuid",
      String(partner.secondary_uuid || "")
    );

    localStorage.setItem(
      "selected_partner_name",
      partner._partnerName || getPartnerName(partner)
    );

    localStorage.setItem(
      "selected_partner_work",
      partner._partnerWork ||
        getPartnerWork(partner, serviceName)
    );

    if (senderChatboxMessage.trim()) {
      localStorage.setItem(
        "selected_sender_chatbox",
        senderChatboxMessage.trim()
      );
    }

    setShowPartnerPopup(false);
    setShowWaitingPopup(true);

    const partnerSecondaryId =
      partner?.secondary_id || partner?.id || "";

    const partnerSecondaryUuid =
      partner?.secondary_uuid || "";

    if (!partnerSecondaryId || !partnerSecondaryUuid) {
      console.warn(
        "Partner secondary_id or secondary_uuid missing — skipping fill sender."
      );
      setShowWaitingPopup(false);
      return;
    }

    if (!routeUserId || !routeUuid) {
      console.warn(
        "User ID or UUID missing from URL — skipping fill sender."
      );
      setShowWaitingPopup(false);
      return;
    }

    const currentLatLong =
      localStorage.getItem("current_latitude_longitude") ||
      "";

    try {
      const formData = new FormData();

      formData.append(
        "partner_secondary_id",
        String(partnerSecondaryId)
      );

      formData.append(
        "partner_secondary_uuid",
        String(partnerSecondaryUuid)
      );

      formData.append("user_id", String(routeUserId));
      formData.append("device_uuid", String(routeUuid));
      formData.append("current_lat_long", currentLatLong);

      formData.append(
        "sender_chatbox",
        String(senderChatboxMessage || "").trim()
      );

      /* ⭐ Charges → sender_money */
      const partnerChargesForMoney =
        partner._partnerCharges ||
        partner.money ||
        partner.charges ||
        partner.charge ||
        partner.price ||
        partner.rate_per_hour ||
        partner.hourly_rate ||
        "";

      formData.append(
        "sender_money",
        String(partnerChargesForMoney)
      );

      /* ⭐ Extra sender fields */
      const partnerName =
        partner._partnerName ||
        partner.full_name ||
        partner.name ||
        "";

      const partnerNumber =
        partner.phone_number ||
        partner.mobile ||
        partner.contact ||
        "";

      const partnerProfile =
        partner._partnerProfile ||
        partner.profile_url ||
        partner.profile ||
        "";

      const partnerAddress =
        partner.address ||
        partner.location ||
        partner.city ||
        "";

      const partnerLatLong =
        partner._latitude && partner._longitude
          ? `${partner._latitude},${partner._longitude}`
          : partner.latitude_longitude || "";

      if (partnerName) {
        formData.append("sender_name", String(partnerName));
      }

      if (partnerNumber) {
        formData.append("sender_number", String(partnerNumber));
      }

      if (partnerProfile) {
        formData.append(
          "sender_profile_images",
          String(partnerProfile)
        );
      }

      if (partnerAddress) {
        formData.append("sender_address", String(partnerAddress));
      }

      if (partnerLatLong) {
        formData.append("sender_lat_long", String(partnerLatLong));
      }

      const response = await fetch(FILL_SENDER_API_URL, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      console.log("FILL SENDER RESULT:", result);

      if (!result?.success) {
        console.warn(
          "Sender details fill failed:",
          result?.message
        );
        setShowWaitingPopup(false);
        return;
      }

      console.log(
        "✅ Sender details filled successfully:",
        result.data
      );

      if (senderChatboxMessage.trim()) {
        setSenderChatboxSaved(true);
      }
    } catch (error) {
      console.error("Fill sender details error:", error);
      setShowWaitingPopup(false);
    }
  };

  /* POLL sender_request VALUE — every 2 seconds */
  useEffect(() => {
    if (!selectedPartner) return;

    const partnerSecondaryId =
      selectedPartner?.secondary_id ||
      selectedPartner?.id ||
      "";

    const partnerSecondaryUuid =
      selectedPartner?.secondary_uuid || "";

    if (!partnerSecondaryId || !partnerSecondaryUuid) {
      return;
    }

    let cancelled = false;

    const checkSenderRequest = async () => {
      try {
        const url =
          `${GET_SENDER_REQUEST_URL}` +
          `?partner_secondary_id=${encodeURIComponent(partnerSecondaryId)}` +
          `&partner_secondary_uuid=${encodeURIComponent(partnerSecondaryUuid)}`;

        const response = await fetch(url, {
          method: "GET",
          headers: { Accept: "application/json" },
          cache: "no-store",
        });

        if (!response.ok) return;

        const result = await response.json();

        if (cancelled) return;

        if (!result?.success) return;

        const requestValue = String(
          result.sender_request ?? ""
        ).trim();

        setSenderRequestValue(requestValue);

        if (requestValue === "1") {
          console.log(
            "✅ Sender accepted — navigating to /requestaccept"
          );

          setShowWaitingPopup(false);

          if (routeUserId && routeUuid) {
            navigate(
              `/${routeUserId}/${routeUuid}/requestaccept`
            );
          } else {
            navigate("/requestaccept");
          }
        }

        if (requestValue === "3") {
          console.log(
            "🚫 Partner busy — showing busy popup"
          );

          setShowWaitingPopup(false);

          setShowPartnerBusyPopup(true);
        }
      } catch (error) {
        /* Silent fail — polling continues */
      }
    };

    checkSenderRequest();

    const interval = setInterval(checkSenderRequest, 2000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [
    selectedPartner,
    routeUserId,
    routeUuid,
    navigate,
  ]);

  /* CONFIRM SELECTED PARTNER */
  const handleConfirmPartner = () => {
    if (
      selectedPartner &&
      typeof onConfirm === "function"
    ) {
      onConfirm(selectedPartner);
      return;
    }

    if (typeof onConfirm === "function") {
      onConfirm();
    }
  };

  /* OPEN USER PROFILE */
  const handleOpenUserProfile = (partner, event) => {
    if (event && event.stopPropagation) {
      event.stopPropagation();
    }

    const id =
      partner?.secondary_id ||
      partner?.user_id ||
      partner?.id ||
      "";

    const uuid =
      partner?.secondary_uuid ||
      partner?.device_uuid ||
      partner?.uuid ||
      "";

    if (!id || !uuid) {
      alert("User ID or Device UUID not found for this partner.");
      return;
    }

    setProfileUserId(String(id));
    setProfileDeviceUuid(String(uuid));
    setShowUserProfile(true);
  };

  /* GOOGLE MAP */
  const mapSrc = hasLocation
    ? `https://maps.google.com/maps?q=${location.latitude},${location.longitude}&z=17&output=embed`
    : "https://maps.google.com/maps?q=India&z=5&output=embed";

  /* TIMER FORMAT */
  const minutes = Math.floor(timer / 60);
  const seconds = timer % 60;

  const formattedTimer = `${String(minutes).padStart(
    2,
    "0"
  )}:${String(seconds).padStart(2, "0")}`;

  /* PARTNER COUNT */
  const nearbyPartnerCount = nearbyPartners.length;

  /* MAIN UI */
  return (
    <div className={styles.screenWrapper}>
      <div className={styles.backgroundGlowOne}></div>
      <div className={styles.backgroundGlowTwo}></div>

      <div className={styles.deviceFrame}>
        {/* HEADER */}
        <header className={styles.appBar}>
          <button
            type="button"
            className={styles.backBtn}
            onClick={handleBack}
            aria-label="Go Back"
          >
            <svg
              width="20"
              height="16"
              viewBox="0 0 24 16"
              fill="none"
              stroke="#005CA9"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M19 8H5" />
              <polyline points="12 1 5 8 12 15" />
            </svg>
          </button>

          <div className={styles.headerBrand}>
            <span className={styles.brandDot}></span>
            <span>BroBizee</span>
          </div>

          <div className={styles.headerStatus}>
            <span></span>
            LIVE
          </div>
        </header>

        {/* SCROLL CONTENT */}
        <div className={styles.scrollContainer}>
          <main className={styles.contentSection}>
            {/* HERO */}
            <section className={styles.heroSection}>
              <div className={styles.serviceIconBox}></div>

              <div className={styles.heroText}>
                <span className={styles.eyebrow}>
                  BROBIZEE ASSURED
                </span>

                <h1
                  className={styles.serviceTitle}
                  title={serviceName}
                >
                  {shortServiceName}
                </h1>

                <div className={styles.searchMessageWrapper}>
                  <p
                    key={searchMessageIndex}
                    className={styles.subtitle}
                  >
                    {searchMessages[searchMessageIndex]}
                  </p>
                </div>
              </div>
            </section>

            {/* SEARCH STATUS */}
            <div className={styles.searchStatusCard}>
              <div className={styles.statusIcon}>
                <span className={styles.statusPulse}></span>
              </div>

              <div className={styles.statusContent}>
                <strong>
                  Finding your nearest {shortServiceName}
                </strong>

                <span>
                  Searching matching professionals around you
                </span>
              </div>

              <div className={styles.statusTimer}>
                {formattedTimer}
              </div>
            </div>

            {/* MAP */}
            <section className={styles.mapSection}>
              <div className={styles.mapHeader}>
                <div>
                  <span className={styles.mapEyebrow}>
                    LIVE LOCATION
                  </span>
                  <h2>Finding Nearby {shortServiceName}</h2>
                </div>

                <div className={styles.liveBadge}>
                  <span></span>
                  LIVE
                </div>
              </div>

              <div className={styles.mapRadarWrapper}>
                <div className={styles.mapCircle}>
                  <iframe
                    title="Current Location Map"
                    className={styles.mapIframe}
                    src={mapSrc}
                    loading="lazy"
                    allowFullScreen
                  ></iframe>

                  <div className={styles.mapOverlay}></div>

                  <div className={styles.searchRadar}>
                    <div className={styles.radarRing}></div>
                    <div className={styles.radarRing}></div>
                    <div className={styles.radarRing}></div>
                    <div className={styles.radarSweep}></div>
                    <div className={styles.radarDot}></div>
                  </div>

                  <div className={styles.searchCorners}>
                    <span className={styles.cornerTopLeft}></span>
                    <span className={styles.cornerTopRight}></span>
                    <span className={styles.cornerBottomLeft}></span>
                    <span className={styles.cornerBottomRight}></span>
                  </div>

                  <div className={styles.locationPin}>
                    <div className={styles.pinOuter}>
                      <div className={styles.pinInner}>●</div>
                    </div>
                  </div>

                  <div className={styles.searchStatus}>
                    <span className={styles.searchPulse}></span>
                    Finding {shortServiceName}...
                  </div>
                </div>
              </div>
            </section>

            {/* 20 KM HIGHLIGHT */}
            <section
              style={{
                marginTop: "14px",
                marginBottom: "14px",
                padding: "15px",
                borderRadius: "22px",
                background:
                  "linear-gradient(135deg,rgba(0,102,204,.10),rgba(255,255,255,.96))",
                border: "1px solid rgba(0,102,204,.14)",
                boxShadow: "0 12px 30px rgba(0,76,150,.09)",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "12px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "11px",
                    minWidth: 0,
                  }}
                >
                  <div
                    style={{
                      width: "45px",
                      height: "45px",
                      minWidth: "45px",
                      borderRadius: "15px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg,#0066cc,#008cff)",
                      color: "#fff",
                      fontSize: "20px",
                      boxShadow: "0 8px 18px rgba(0,102,204,.25)",
                    }}
                  >
                    👷
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: "10px",
                        fontWeight: 900,
                        letterSpacing: "1px",
                        color: "#0066cc",
                      }}
                    >
                      {shortServiceName.toUpperCase()}
                    </div>

                    <div
                      style={{
                        fontSize: "13px",
                        fontWeight: 800,
                        color: "#18212b",
                        marginTop: "2px",
                      }}
                    >
                      Matching Partners
                    </div>

                    <div
                      style={{
                        fontSize: "10px",
                        color: "#707b87",
                        marginTop: "2px",
                      }}
                    >
                      Within 20 KM
                    </div>
                  </div>
                </div>

                <div
                  style={{
                    minWidth: "72px",
                    textAlign: "center",
                    padding: "8px 10px",
                    borderRadius: "16px",
                    background: "#fff",
                    border: "1px solid rgba(0,102,204,.12)",
                  }}
                >
                  <div
                    style={{
                      fontSize: "27px",
                      lineHeight: 1,
                      fontWeight: 900,
                      color: "#0066cc",
                    }}
                  >
                    {partnerLoading ? "..." : nearbyPartnerCount}
                  </div>

                  <div
                    style={{
                      fontSize: "8px",
                      fontWeight: 900,
                      color: "#69727c",
                      marginTop: "4px",
                    }}
                  >
                    PARTNERS
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "8px",
                }}
              >
                <span
                  style={{
                    fontSize: "10px",
                    color:
                      nearbyPartnerCount > 0 ? "#198754" : "#777",
                    fontWeight: 800,
                  }}
                >
                  {partnerLoading
                    ? "Searching matching partners..."
                    : nearbyPartnerCount > 0
                    ? `${nearbyPartnerCount} matching partner${
                        nearbyPartnerCount === 1 ? "" : "s"
                      } within 20 KM`
                    : `No ${shortServiceName} partner within 20 KM`}
                </span>

                {nearbyPartnerCount > 0 && (
                  <button
                    type="button"
                    onClick={() => setShowPartnerPopup(true)}
                    style={{
                      border: "none",
                      background: "#0066cc",
                      color: "#fff",
                      borderRadius: "12px",
                      padding: "7px 11px",
                      fontSize: "10px",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    View Partners
                  </button>
                )}
              </div>
            </section>

            {/* LOCATION PERMISSION */}
            {permissionState === "denied" && (
              <div className={styles.permissionCard}>
                <div className={styles.permissionIcon}>📍</div>
                <div className={styles.permissionTitle}>
                  Location Permission Required
                </div>
                <div className={styles.permissionText}>
                  Please allow location access to find nearby{" "}
                  {shortServiceName}.
                </div>
                <button
                  type="button"
                  className={styles.locationButton}
                  onClick={handleEnableLocation}
                >
                  Enable Location
                </button>
              </div>
            )}

            {/* GPS WARNING */}
            {(permissionState === "unavailable" ||
              permissionState === "timeout") && (
              <div className={styles.warningCard}>
                <div className={styles.warningIcon}>⚠</div>
                <div className={styles.warningContent}>
                  <strong>Turn ON Device Location</strong>
                  <span>
                    Please turn ON GPS/Location services and try
                    again.
                  </span>
                </div>
                <button
                  type="button"
                  className={styles.locationButton}
                  onClick={handleEnableLocation}
                >
                  Retry Location
                </button>
              </div>
            )}

            {/* LOCATION CARD */}
            <section className={styles.locationCard}>
              <div className={styles.locationCardTop}>
                <div className={styles.locationMainIcon}>📍</div>

                <div className={styles.locationTextBlock}>
                  <span className={styles.locationLabel}>
                    MY CURRENT LOCATION
                  </span>
                  <h2 className={styles.locationHeader}>
                    {locationLoading
                      ? "Detecting location..."
                      : "Location detected"}
                  </h2>
                </div>

                <div
                  className={`${styles.locationCheck} ${
                    hasLocation ? styles.locationCheckActive : ""
                  }`}
                >
                  {hasLocation ? "✓" : "•"}
                </div>
              </div>

              <p className={styles.locationAddress}>
                {locationAddress}
              </p>

              {hasLocation && (
                <div className={styles.coordinatesCard}>
                  <div className={styles.coordinateItem}>
                    <span>LAT</span>
                    <strong>
                      {location.latitude.toFixed(8)}
                    </strong>
                  </div>

                  <div className={styles.coordinateDivider}></div>

                  <div className={styles.coordinateItem}>
                    <span>LONG</span>
                    <strong>
                      {location.longitude.toFixed(8)}
                    </strong>
                  </div>
                </div>
              )}

              {locationError && (
                <div className={styles.locationError}>
                  {locationError}
                </div>
              )}

              {!locationLoading && (
                <button
                  type="button"
                  className={styles.refreshLocation}
                  onClick={handleEnableLocation}
                >
                  ↻ Refresh Location
                </button>
              )}
            </section>

            {/* API ERROR */}
            {partnerError && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "11px 13px",
                  borderRadius: "14px",
                  background: "#fff5f5",
                  border: "1px solid #ffd8d8",
                  color: "#c62828",
                  fontSize: "11px",
                  lineHeight: 1.4,
                }}
              >
                {partnerError}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* =========================================================
          MATCHING PARTNERS POPUP
      ========================================================= */}

      {showPartnerPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(8,20,34,.68)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "14px",
          }}
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setShowPartnerPopup(false);
              setChatInput("");
              setChatMessages([
                {
                  id: 1,
                  from: "bot",
                  text:
                    "Hi 👋 I'm BroBizee Assistant. Tell me what you need — e.g. 'Please bring your own tools' — and I'll attach it to your partner request.",
                  time: new Date().toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  }),
                },
              ]);
            }
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "440px",
              maxHeight: "92vh",
              overflowY: "auto",
              overflowX: "hidden",
              borderRadius: "30px",
              background:
                "linear-gradient(180deg,#ffffff,#f5f8fc)",
              boxShadow: "0 30px 90px rgba(0,0,0,.35)",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "thin",
              scrollbarColor: "#c1cbd6 #f0f4f8",
              overscrollBehavior: "contain",
              position: "relative",
            }}
          >
            {/* TOP LINE */}
            <div
              style={{
                height: "5px",
                background:
                  "linear-gradient(90deg,#0066cc,#00a8ff,#ff6a00)",
              }}
            ></div>

            {/* HEADER */}
            <div
              style={{
                padding: "17px 17px 13px",
                borderBottom: "1px solid #e9eef3",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    gap: "11px",
                    alignItems: "center",
                  }}
                >
                  <div
                    style={{
                      width: "49px",
                      height: "49px",
                      borderRadius: "17px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      background:
                        "linear-gradient(135deg,#0066cc,#008cff)",
                      color: "#fff",
                      fontSize: "22px",
                      boxShadow:
                        "0 10px 25px rgba(0,102,204,.25)",
                    }}
                  >
                    👷
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "9px",
                        fontWeight: 900,
                        letterSpacing: "1.2px",
                        color: "#0066cc",
                      }}
                    >
                      BROBIZEE ASSURED
                    </div>

                    <h2
                      style={{
                        margin: "3px 0 0",
                        fontSize: "20px",
                        color: "#17212b",
                        fontWeight: 900,
                      }}
                    >
                      {shortServiceName}
                    </h2>

                    <div
                      style={{
                        marginTop: "3px",
                        fontSize: "10px",
                        color: "#73808c",
                      }}
                    >
                      Matching partners near your location
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setShowPartnerPopup(false);
                    setChatInput("");
                    setChatMessages([
                      {
                        id: 1,
                        from: "bot",
                        text:
                          "Hi 👋 I'm BroBizee Assistant. Tell me what you need — e.g. 'Please bring your own tools' — and I'll attach it to your partner request.",
                        time: new Date().toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        }),
                      },
                    ]);
                  }}
                  style={{
                    width: "34px",
                    height: "34px",
                    borderRadius: "50%",
                    border: "1px solid #e0e7ee",
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: "20px",
                    color: "#52606d",
                  }}
                >
                  ×
                </button>
              </div>

              {/* COUNT */}
              <div
                style={{
                  marginTop: "14px",
                  padding: "12px 14px",
                  borderRadius: "18px",
                  background:
                    "linear-gradient(135deg,#eaf4ff,#f8fbff)",
                  border: "1px solid #d9eaff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <div
                    style={{
                      fontSize: "9px",
                      color: "#0066cc",
                      fontWeight: 900,
                      letterSpacing: ".8px",
                    }}
                  >
                    PARTNERS WITHIN 20 KM
                  </div>

                  <div
                    style={{
                      marginTop: "3px",
                      fontSize: "10px",
                      color: "#6d7782",
                    }}
                  >
                    Same service • Nearest first
                  </div>
                </div>

                <strong
                  style={{
                    fontSize: "28px",
                    lineHeight: 1,
                    color: "#0066cc",
                    fontWeight: 900,
                  }}
                >
                  {nearbyPartnerCount}
                </strong>
              </div>
            </div>

            {/* PARTNER LIST */}
            <div
              style={{
                maxHeight: "56vh",
                overflowY: "auto",
                padding: "12px 13px 16px",
              }}
            >
              {nearbyPartners.map((partner, index) => {
                const online = isPartnerOnline(
                  partner._partnerStatus
                );

                const profile = partner._partnerProfile;
                const rating = partner._partnerRating;
                const views = partner._partnerViews;
                const charges = partner._partnerCharges;
                const team = partner._partnerTeam;
                const experience = partner._partnerExperience;
                const age = partner._partnerAge;
                const mode = partner._partnerMode;

                const isSelected =
                  selectedPartner === partner ||
                  (selectedPartner &&
                    selectedPartner.id &&
                    partner.id &&
                    String(selectedPartner.id) ===
                      String(partner.id));

                return (
                  <div
                    key={
                      partner.id ||
                      partner.secondary_uuid ||
                      partner.secondary_id ||
                      `partner-${index}`
                    }
                    style={{
                      position: "relative",
                      marginBottom: "11px",
                      padding: "13px",
                      borderRadius: "21px",
                      background: "#fff",
                      border: isSelected
                        ? "2px solid #198754"
                        : "1px solid #e4ebf1",
                      boxShadow: isSelected
                        ? "0 12px 28px rgba(25,135,84,.13)"
                        : "0 7px 20px rgba(20,40,60,.06)",
                    }}
                  >
                    {/* MATCH BADGE */}
                    <div
                      style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        fontSize: "7px",
                        fontWeight: 900,
                        color: "#0066cc",
                        background: "#eaf4ff",
                        padding: "4px 6px",
                        borderRadius: "7px",
                        letterSpacing: ".4px",
                      }}
                    >
                      {shortServiceName.toUpperCase()} MATCH
                    </div>

                    {/* TOP */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "11px",
                        paddingRight: "75px",
                      }}
                    >
                      <div
                        style={{
                          width: "54px",
                          height: "54px",
                          minWidth: "54px",
                          borderRadius: "17px",
                          overflow: "hidden",
                          background:
                            "linear-gradient(135deg,#eaf4ff,#dceeff)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#0066cc",
                          fontSize: "19px",
                          fontWeight: 900,
                        }}
                      >
                        {profile ? (
                          <img
                            src={profile}
                            alt={partner._partnerName}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            onError={(event) => {
                              event.currentTarget.style.display =
                                "none";
                            }}
                          />
                        ) : (
                          <span>
                            {String(
                              partner._partnerName || "P"
                            )
                              .trim()
                              .charAt(0)
                              .toUpperCase()}
                          </span>
                        )}
                      </div>

                      <div style={{ minWidth: 0, flex: 1 }}>
                        <h3
                          style={{
                            margin: 0,
                            fontSize: "15px",
                            fontWeight: 900,
                            color: "#1b2732",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            position: "relative",
                          }}
                        >
                          <span
                            style={{
                              display: "inline-block",
                              whiteSpace: "nowrap",
                              paddingRight: "40px",
                              animation:
                                (partner._partnerName?.length || 0) > 18
                                  ? "partnerNameMarquee 10s linear infinite"
                                  : "none",
                            }}
                          >
                            {partner._partnerName}
                          </span>

                          <style>
                            {`
                              @keyframes partnerNameMarquee {
                                0% {
                                  transform: translateX(0);
                                }
                                100% {
                                  transform: translateX(-50%);
                                }
                              }
                            `}
                          </style>
                        </h3>

                        <div
                          style={{
                            marginTop: "4px",
                            fontSize: "10px",
                            color: "#687582",
                            fontWeight: 700,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {partner._partnerWork}
                        </div>

                        <div
                          style={{
                            marginTop: "5px",
                            display: "flex",
                            alignItems: "center",
                            gap: "5px",
                            flexWrap: "wrap",
                          }}
                        >
                          <span
                            style={{
                              fontSize: "8px",
                              color: "#0066cc",
                              background: "#eaf4ff",
                              padding: "4px 6px",
                              borderRadius: "7px",
                              fontWeight: 900,
                            }}
                          >
                            💎 BroBizee. Assured
                          </span>

                          {online === true && (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "4px",
                                fontSize: "8px",
                                color: "#16834a",
                                background: "#eaf8f0",
                                padding: "4px 6px",
                                borderRadius: "7px",
                                fontWeight: 900,
                              }}
                            >
                              <span
                                style={{
                                  width: "6px",
                                  height: "6px",
                                  borderRadius: "50%",
                                  background: "#20a45a",
                                }}
                              ></span>
                              ONLINE
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        style={{
                          minWidth: "55px",
                          textAlign: "right",
                        }}
                      >
                        <strong
                          style={{
                            display: "block",
                            fontSize: "14px",
                            color: "#0066cc",
                            fontWeight: 900,
                          }}
                        >
                          {formatDistance(partner._distanceKm)}
                        </strong>

                        <span
                          style={{
                            display: "block",
                            marginTop: "2px",
                            fontSize: "7px",
                            color: "#7b858f",
                            fontWeight: 800,
                          }}
                        >
                          AWAY
                        </span>
                      </div>
                    </div>

                    {/* DATA GRID */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3,1fr)",
                        gap: "7px",
                        marginTop: "12px",
                        paddingTop: "11px",
                        borderTop: "1px solid #edf1f4",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          RATING
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "11px",
                            fontWeight: 900,
                            color: "#e39b00",
                          }}
                        >
                          ⭐ {rating || "New"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          VIEWS
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "11px",
                            fontWeight: 900,
                            color: "#344454",
                          }}
                        >
                          {views || "—"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          CHARGES
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "11px",
                            fontWeight: 900,
                            color: "#198754",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {charges ? `₹${charges}` : "—"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          TEAM
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "10px",
                            fontWeight: 900,
                            color: "#344454",
                          }}
                        >
                          {team || "Individual"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          EXP
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "10px",
                            fontWeight: 900,
                            color: "#344454",
                          }}
                        >
                          {experience || "—"}
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "8px",
                          borderRadius: "11px",
                          background: "#f8fafc",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "8px",
                            color: "#87919b",
                            fontWeight: 700,
                          }}
                        >
                          AGE
                        </div>

                        <div
                          style={{
                            marginTop: "3px",
                            fontSize: "10px",
                            fontWeight: 900,
                            color: "#344454",
                          }}
                        >
                          {age ? `${age} Yrs` : "—"}
                        </div>
                      </div>
                    </div>

                    {/* MODE + GENDER */}
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                        marginTop: "9px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          flexWrap: "wrap",
                        }}
                      >
                        {mode && (
                          <span
                            style={{
                              fontSize: "8px",
                              padding: "5px 7px",
                              borderRadius: "8px",
                              background: "#eef5fb",
                              color: "#4d6070",
                              fontWeight: 800,
                            }}
                          >
                            MODE: {mode}
                          </span>
                        )}

                        {partner._partnerGender && (
                          <span
                            style={{
                              fontSize: "8px",
                              padding: "5px 7px",
                              borderRadius: "8px",
                              background: "#eef5fb",
                              color: "#4d6070",
                              fontWeight: 800,
                            }}
                          >
                            {partner._partnerGender}
                          </span>
                        )}
                      </div>

                      <span
                        style={{
                          fontSize: "9px",
                          fontWeight: 900,
                          color: "#198754",
                        }}
                      >
                        ✓ VERIFIED
                      </span>
                    </div>

                    {/* SELECT PARTNER */}
                    <button
                      type="button"
                      onClick={() => handleSelectPartner(partner)}
                      disabled={senderChatboxSaving}
                      style={{
                        width: "100%",
                        marginTop: "11px",
                        border: "none",
                        borderRadius: "13px",
                        padding: "11px",
                        background: isSelected
                          ? "#198754"
                          : "linear-gradient(135deg,#0066cc,#008cff)",
                        color: "#fff",
                        fontSize: "10px",
                        fontWeight: 900,
                        cursor: senderChatboxSaving
                          ? "not-allowed"
                          : "pointer",
                        opacity: senderChatboxSaving ? 0.7 : 1,
                        boxShadow:
                          "0 8px 18px rgba(0,102,204,.18)",
                      }}
                    >
                      {senderChatboxSaving
                        ? "SAVING MESSAGE..."
                        : isSelected
                        ? "✓ PARTNER SELECTED"
                        : "SELECT THIS PARTNER"}
                    </button>

                    {/* VIEW USER PROFILE */}
                    <button
                      type="button"
                      onClick={(e) =>
                        handleOpenUserProfile(partner, e)
                      }
                      style={{
                        width: "100%",
                        marginTop: "7px",
                        border: "1.5px solid #0066cc",
                        borderRadius: "13px",
                        padding: "10px",
                        background: "#ffffff",
                        color: "#0066cc",
                        fontSize: "10px",
                        fontWeight: 900,
                        cursor: "pointer",
                        letterSpacing: "0.4px",
                        display: "none",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "6px",
                      }}
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <circle cx="12" cy="8" r="4" />
                        <path d="M4 21c0-4.2 3.6-7 8-7s8 2.8 8 7" />
                      </svg>
                      VIEW USER PROFILE
                    </button>
                  </div>
                );
              })}
            </div>

            {/* FOOTER */}
            <div
              style={{
                padding: "12px 14px 15px",
                borderTop: "1px solid #e9eef3",
                background: "#f8fafc",
                display: "flex",
                gap: "8px",
              }}
            >
              <button
                type="button"
                onClick={handleRefreshPartners}
                disabled={partnerLoading}
                style={{
                  flex: 1,
                  border: "1px solid #dce4eb",
                  borderRadius: "13px",
                  padding: "11px",
                  background: "#fff",
                  color: "#42515f",
                  fontSize: "10px",
                  fontWeight: 800,
                  cursor: partnerLoading
                    ? "not-allowed"
                    : "pointer",
                  opacity: partnerLoading ? 0.6 : 1,
                }}
              >
                {partnerLoading ? "Searching..." : "↻ Refresh"}
              </button>

              <button
                type="button"
                onClick={handleConfirmPartner}
                disabled={!selectedPartner}
                style={{
                  flex: 1.5,
                  border: "none",
                  borderRadius: "13px",
                  padding: "11px",
                  background: selectedPartner
                    ? "linear-gradient(135deg,#198754,#24a866)"
                    : "#cbd3da",
                  color: "#fff",
                  fontSize: "10px",
                  fontWeight: 900,
                  cursor: selectedPartner
                    ? "pointer"
                    : "not-allowed",
                }}
              >
                {selectedPartner
                  ? "CONFIRM PARTNER →"
                  : "SELECT PARTNER →"}
              </button>
            </div>

            {/* ⭐ FLOATING CHAT BUTTON (Inside Partner Popup) */}
            <button
              type="button"
              className={styles.chatFab}
              onClick={() => setChatPanelOpen((v) => !v)}
              aria-label="Open chat assistant"
            >
              {chatPanelOpen ? (
                <svg
                  width="22"
                  height="22"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                >
                  <line x1="6" y1="6" x2="18" y2="18" />
                  <line x1="18" y1="6" x2="6" y2="18" />
                </svg>
              ) : (
                <>
                  <svg
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#fff"
                    strokeWidth="2.2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>

                  {senderChatboxMessage.trim() && (
                    <span className={styles.chatFabDot} />
                  )}
                </>
              )}
            </button>

            {/* ⭐ CHAT PANEL */}
            {chatPanelOpen && (
              <div
                className={styles.chatPanel}
                role="dialog"
                aria-label="Chat assistant"
              >
                {/* HEADER */}
                <div className={styles.chatPanelHeader}>
                  <div className={styles.chatPanelAvatar}>🤖</div>

                  <div className={styles.chatPanelHeaderText}>
                    <strong>BroBizee Assistant</strong>
                    <span>
                      <span className={styles.chatOnlineDot} /> Online
                    </span>
                  </div>

                  <button
                    type="button"
                    className={styles.chatPanelClose}
                    onClick={() => setChatPanelOpen(false)}
                    aria-label="Close chat"
                  >
                    ×
                  </button>
                </div>

                {/* BODY */}
                <div className={styles.chatPanelBody}>
                  {chatMessages.map((msg) => (
                    <div
                      key={msg.id}
                      className={
                        msg.from === "user"
                          ? styles.chatBubbleUser
                          : styles.chatBubbleBot
                      }
                    >
                      <div className={styles.chatBubbleText}>
                        {msg.text}
                      </div>
                      <div className={styles.chatBubbleTime}>
                        {msg.time}
                      </div>
                    </div>
                  ))}

                  <div ref={chatEndRef} />
                </div>

                {/* FOOTER */}
                <div className={styles.chatPanelFooter}>
                  <input
                    type="text"
                    className={styles.chatInput}
                    placeholder="Type your message..."
                    value={chatInput}
                    maxLength={200}
                    onChange={(e) => setChatInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleSendChatMessage();
                      }
                    }}
                  />

                  <button
                    type="button"
                    className={styles.chatSendBtn}
                    onClick={handleSendChatMessage}
                    disabled={!chatInput.trim()}
                    aria-label="Send message"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.4"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                </div>

                {/* COUNTER */}
                <div className={styles.chatCounter}>
                  {chatInput.length}/200
                  {senderChatboxSaved && (
                    <span className={styles.chatSavedBadge}>
                      ✓ Ready to send
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* =========================================================
          WAITING FOR PARTNER POPUP
      ========================================================= */}

      {showWaitingPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            background: "rgba(8,18,30,.78)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "400px",
              borderRadius: "30px",
              padding: "30px 24px 24px",
              background:
                "linear-gradient(180deg,#ffffff,#f0f7ff)",
              boxShadow:
                "0 30px 90px rgba(0,0,0,.4), 0 0 0 1px rgba(0,102,204,.06)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              animation:
                "waitingPopupIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "5px",
                background:
                  "linear-gradient(90deg,#005CA9,#00a8ff,#00d4a0)",
              }}
            ></div>

            <div
              style={{
                width: "110px",
                height: "110px",
                margin: "5px auto 20px",
                position: "relative",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  borderRadius: "50%",
                  border: "3px solid rgba(0,102,204,.12)",
                  animation:
                    "waitingOuterPulse 2s ease-out infinite",
                }}
              />

              <div
                style={{
                  position: "absolute",
                  inset: "12px",
                  borderRadius: "50%",
                  border: "2px solid rgba(0,102,204,.18)",
                  animation:
                    "waitingOuterPulse 2s ease-out infinite 0.5s",
                }}
              />

              <div
                style={{
                  width: "70px",
                  height: "70px",
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg,#005CA9,#00a8ff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "28px",
                  boxShadow:
                    "0 15px 35px rgba(0,102,204,.35), inset 0 2px 3px rgba(255,255,255,.3)",
                  animation:
                    "waitingIconFloat 2.4s ease-in-out infinite",
                  position: "relative",
                  zIndex: 3,
                }}
              >
                <svg
                  width="70"
                  height="70"
                  viewBox="0 0 70 70"
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    animation:
                      "waitingSpinRing 2s linear infinite",
                  }}
                >
                  <circle
                    cx="35"
                    cy="35"
                    r="32"
                    fill="none"
                    stroke="rgba(255,255,255,.35)"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeDasharray="50 150"
                  />
                </svg>

                ⏳
              </div>

              <span
                style={{
                  position: "absolute",
                  right: "6px",
                  bottom: "14px",
                  width: "16px",
                  height: "16px",
                  borderRadius: "50%",
                  background: "#22c55e",
                  border: "3px solid #ffffff",
                  boxShadow:
                    "0 0 0 0 rgba(34, 197, 94, 0.6)",
                  animation:
                    "waitingLivePulse 1.6s ease-out infinite",
                  zIndex: 4,
                }}
              />
            </div>

            <div
              style={{
                fontSize: "9px",
                fontWeight: 900,
                color: "#005CA9",
                letterSpacing: "1.4px",
                marginBottom: "6px",
              }}
            >
              REQUEST SENT
            </div>

            <h2
              style={{
                margin: "0 0 10px",
                fontSize: "20px",
                fontWeight: 900,
                color: "#1b2732",
                lineHeight: 1.3,
              }}
            >
              Waiting for Partner
            </h2>

            <p
              style={{
                margin: "0 auto 18px",
                maxWidth: "320px",
                fontSize: "12.5px",
                lineHeight: 1.6,
                color: "#5a6672",
              }}
            >
              Please wait a moment.
              <br />
              Partner will accept your request shortly...
            </p>

            <div
              style={{
                width: "100%",
                maxWidth: "300px",
                margin: "0 auto 18px",
              }}
            >
              <div
                style={{
                  width: "100%",
                  height: "6px",
                  borderRadius: "999px",
                  background: "rgba(0,102,204,.08)",
                  overflow: "hidden",
                  position: "relative",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    height: "100%",
                    width: "40%",
                    borderRadius: "999px",
                    background:
                      "linear-gradient(90deg,#005CA9,#00a8ff,#00d4a0)",
                    animation:
                      "waitingProgressSlide 1.6s ease-in-out infinite",
                    boxShadow:
                      "0 0 12px rgba(0,168,255,.5)",
                  }}
                />
              </div>
            </div>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                marginBottom: "16px",
              }}
            >
              {[0, 1, 2].map((i) => (
                <span
                  key={i}
                  style={{
                    width: "8px",
                    height: "8px",
                    borderRadius: "50%",
                    background: "#005CA9",
                    opacity: 0.4,
                    animation: `waitingDotPulse 1.4s ease-in-out infinite ${
                      i * 0.2
                    }s`,
                  }}
                />
              ))}
            </div>

            <div
              style={{
                padding: "12px 14px",
                borderRadius: "14px",
                background:
                  "linear-gradient(135deg,#f0f7ff,#f8fbff)",
                border: "1px solid #d9eaff",
                display: "flex",
                alignItems: "center",
                gap: "10px",
                textAlign: "left",
                marginBottom: "16px",
              }}
            >
              <div
                style={{
                  width: "36px",
                  height: "36px",
                  minWidth: "36px",
                  borderRadius: "11px",
                  background:
                    "linear-gradient(135deg,#005CA9,#00a8ff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "16px",
                  boxShadow:
                    "0 6px 14px rgba(0,102,204,.22)",
                }}
              >
                🔔
              </div>

              <div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 900,
                    color: "#005CA9",
                    letterSpacing: ".5px",
                    marginBottom: "2px",
                  }}
                >
                  AUTO NOTIFICATION
                </div>

                <div
                  style={{
                    fontSize: "10.5px",
                    color: "#5a6672",
                    fontWeight: 600,
                    lineHeight: 1.4,
                  }}
                >
                  You'll be notified as soon as the partner
                  responds.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowWaitingPopup(false);
                setSelectedPartner(null);
              }}
              style={{
                width: "100%",
                border: "1px solid #dce4eb",
                borderRadius: "14px",
                padding: "12px",
                background: "#fff",
                color: "#52606d",
                fontSize: "11px",
                fontWeight: 800,
                cursor: "pointer",
                letterSpacing: "0.3px",
              }}
            >
              Cancel Request
            </button>

            <div
              style={{
                marginTop: "14px",
                fontSize: "9px",
                color: "#a0a8b3",
                fontWeight: 600,
                letterSpacing: ".3px",
              }}
            >
              BroBizee ensures fair service matching 💙
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          PARTNER NOT AVAILABLE POPUP
      ========================================================= */}

      {showBusyPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10000,
            background: "rgba(8,18,30,.72)",
            backdropFilter: "blur(12px)",
            WebkitBackdropFilter: "blur(12px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "390px",
              borderRadius: "30px",
              padding: "25px 20px 20px",
              background:
                "linear-gradient(180deg,#ffffff,#f7faff)",
              boxShadow: "0 30px 80px rgba(0,0,0,.35)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "5px",
                background:
                  "linear-gradient(90deg,#ff5e00,#ff8a3d,#0066cc)",
              }}
            ></div>

            <div
              style={{
                width: "72px",
                height: "72px",
                margin: "5px auto 14px",
                borderRadius: "25px",
                background:
                  "linear-gradient(135deg,#fff1e9,#fff8f3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "31px",
              }}
            >
              😔
            </div>

            <div
              style={{
                fontSize: "9px",
                fontWeight: 900,
                color: "#ff6500",
                letterSpacing: "1.1px",
              }}
            >
              BROBIZEE SEARCH
            </div>

            <h2
              style={{
                margin: "7px 0 7px",
                fontSize: "22px",
                fontWeight: 900,
                color: "#1b2732",
              }}
            >
              Sorry, Partners Not Available
            </h2>

            <p
              style={{
                margin: "0 auto",
                maxWidth: "320px",
                fontSize: "11px",
                lineHeight: 1.6,
                color: "#707b86",
              }}
            >
              Sorry, we couldn't find a{" "}
              <strong style={{ color: "#0066cc" }}>
                {shortServiceName}
              </strong>{" "}
              partner within <strong>20 KM</strong> of your
              current location.
            </p>

            <div
              style={{
                marginTop: "15px",
                padding: "12px",
                borderRadius: "17px",
                background: "#f3f7fb",
                border: "1px solid #e3ebf2",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div style={{ textAlign: "left" }}>
                <div
                  style={{
                    fontSize: "8px",
                    color: "#89949f",
                    fontWeight: 800,
                  }}
                >
                  SEARCHED SERVICE
                </div>

                <div
                  style={{
                    marginTop: "3px",
                    fontSize: "12px",
                    color: "#273541",
                    fontWeight: 900,
                  }}
                >
                  {shortServiceName}
                </div>
              </div>

              <div style={{ textAlign: "right" }}>
                <div
                  style={{
                    fontSize: "8px",
                    color: "#89949f",
                    fontWeight: 800,
                  }}
                >
                  SEARCH RANGE
                </div>

                <div
                  style={{
                    marginTop: "3px",
                    fontSize: "12px",
                    color: "#0066cc",
                    fontWeight: 900,
                  }}
                >
                  20 KM
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={handleFindPartnerAgain}
              style={{
                width: "100%",
                marginTop: "16px",
                border: "none",
                borderRadius: "14px",
                padding: "13px",
                background:
                  "linear-gradient(135deg,#0066cc,#008cff)",
                color: "#fff",
                fontSize: "11px",
                fontWeight: 900,
                cursor: "pointer",
                boxShadow:
                  "0 10px 24px rgba(0,102,204,.22)",
              }}
            >
              🔄 Search Partner Again
            </button>

            <button
              type="button"
              onClick={handleBack}
              style={{
                width: "100%",
                marginTop: "8px",
                border: "1px solid #dce4eb",
                borderRadius: "14px",
                padding: "12px",
                background: "#fff",
                color: "#52606d",
                fontSize: "10px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Go Back
            </button>
          </div>
        </div>
      )}

      {/* =========================================================
          PARTNER BUSY POPUP
      ========================================================= */}

      {showPartnerBusyPopup && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10001,
            background: "rgba(8,18,30,.78)",
            backdropFilter: "blur(14px)",
            WebkitBackdropFilter: "blur(14px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: "400px",
              borderRadius: "30px",
              padding: "25px 22px 22px",
              background:
                "linear-gradient(180deg,#ffffff,#fff9f5)",
              boxShadow:
                "0 30px 90px rgba(0,0,0,.4), 0 0 0 1px rgba(255,107,0,.06)",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              animation:
                "partnerBusyPopupIn 0.4s cubic-bezier(0.16, 1, 0.3, 1)",
            }}
          >
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "5px",
                background:
                  "linear-gradient(90deg,#ff5e00,#ff8a3d,#ffb066)",
              }}
            ></div>

            <div
              style={{
                width: "86px",
                height: "86px",
                margin: "5px auto 16px",
                borderRadius: "28px",
                background:
                  "linear-gradient(135deg,#fff1e9,#ffe4d1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "38px",
                boxShadow:
                  "0 12px 26px rgba(255,107,0,.18)",
              }}
            >
              🚧
            </div>

            <div
              style={{
                fontSize: "9px",
                fontWeight: 900,
                color: "#ff6500",
                letterSpacing: "1.4px",
              }}
            >
              PARTNER UNAVAILABLE
            </div>

            <h2
              style={{
                margin: "8px 0 10px",
                fontSize: "21px",
                fontWeight: 900,
                color: "#1b2732",
                lineHeight: 1.25,
              }}
            >
              The partner has cancelled your request.
            </h2>

            <p
              style={{
                margin: "0 auto",
                maxWidth: "330px",
                fontSize: "12px",
                lineHeight: 1.6,
                color: "#707b86",
              }}
            >
              The selected partner is{" "}
              <strong style={{ color: "#ff6500" }}>
                currently engaged in another work
              </strong>{" "}
              and is unable to take your request right now.
            </p>

            <div
              style={{
                height: "1px",
                background: "#eef1f5",
                margin: "18px 0 16px",
              }}
            ></div>

            <div
              style={{
                padding: "14px 12px",
                borderRadius: "16px",
                background:
                  "linear-gradient(135deg,#f0f7ff,#f8fbff)",
                border: "1px solid #d9eaff",
                display: "flex",
                alignItems: "center",
                gap: "11px",
                textAlign: "left",
                marginBottom: "18px",
              }}
            >
              <div
                style={{
                  width: "42px",
                  height: "42px",
                  minWidth: "42px",
                  borderRadius: "13px",
                  background:
                    "linear-gradient(135deg,#0066cc,#008cff)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#fff",
                  fontSize: "18px",
                  boxShadow:
                    "0 8px 18px rgba(0,102,204,.22)",
                }}
              >
                💡
              </div>

              <div>
                <div
                  style={{
                    fontSize: "10px",
                    fontWeight: 900,
                    color: "#0066cc",
                    letterSpacing: ".6px",
                    marginBottom: "3px",
                  }}
                >
                  TRY ANOTHER PARTNER
                </div>

                <div
                  style={{
                    fontSize: "11px",
                    color: "#5a6672",
                    fontWeight: 600,
                    lineHeight: 1.45,
                  }}
                >
                  There are other professionals available
                  nearby who can help you right now.
                </div>
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setShowPartnerBusyPopup(false);
                setSelectedPartner(null);
                setSenderRequestValue(null);

                handleFindPartnerAgain();
              }}
              style={{
                width: "100%",
                border: "none",
                borderRadius: "14px",
                padding: "14px",
                background:
                  "linear-gradient(135deg,#0066cc,#008cff)",
                color: "#fff",
                fontSize: "12px",
                fontWeight: 900,
                cursor: "pointer",
                letterSpacing: "0.4px",
                boxShadow:
                  "0 12px 26px rgba(0,102,204,.28)",
                transition:
                  "transform 0.2s ease, box-shadow 0.2s ease",
              }}
              onMouseDown={(e) => {
                e.currentTarget.style.transform = "scale(0.97)";
              }}
              onMouseUp={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "scale(1)";
              }}
            >
              🔄 Find Another Partner
            </button>

            <button
              type="button"
              onClick={() => {
                setShowPartnerBusyPopup(false);
                handleBack();
              }}
              style={{
                width: "100%",
                marginTop: "9px",
                border: "1px solid #dce4eb",
                borderRadius: "14px",
                padding: "12px",
                background: "#fff",
                color: "#52606d",
                fontSize: "11px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Go Back
            </button>

            <div
              style={{
                marginTop: "14px",
                fontSize: "9px",
                color: "#a0a8b3",
                fontWeight: 600,
                letterSpacing: ".3px",
              }}
            >
              BroBizee ensures fair service matching 💙
            </div>
          </div>
        </div>
      )}

      {/* =========================================================
          USER PROFILE POPUP
      ========================================================= */}

      {showUserProfile && (
        <UserProfilePopup
          userId={profileUserId}
          deviceUuid={profileDeviceUuid}
          onClose={() => {
            setShowUserProfile(false);
            setProfileUserId("");
            setProfileDeviceUuid("");
          }}
        />
      )}

      {/* ANIMATIONS */}
      <style>
        {`
          @keyframes partnerBusyPopupIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes waitingPopupIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }

          @keyframes waitingOuterPulse {
            0% {
              transform: scale(0.85);
              opacity: 0.9;
            }
            100% {
              transform: scale(1.25);
              opacity: 0;
            }
          }

          @keyframes waitingIconFloat {
            0%, 100% {
              transform: translateY(0);
            }
            50% {
              transform: translateY(-6px);
            }
          }

          @keyframes waitingSpinRing {
            from {
              transform: rotate(0deg);
            }
            to {
              transform: rotate(360deg);
            }
          }

          @keyframes waitingLivePulse {
            0% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0.6);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(34, 197, 94, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(34, 197, 94, 0);
            }
          }

          @keyframes waitingProgressSlide {
            0% {
              left: -40%;
            }
            100% {
              left: 100%;
            }
          }

          @keyframes waitingDotPulse {
            0%, 80%, 100% {
              opacity: 0.3;
              transform: scale(0.85);
            }
            40% {
              opacity: 1;
              transform: scale(1.15);
            }
          }
        `}
      </style>
    </div>
  );
}